// ResilienceWatcher — watches for setbacks (a loss week or a Freedom-Meter drop)
// and for comebacks. On a loss it shows the ResilienceScreen and flags a slump; on
// the first profitable week after a slump it awards +5 Persistence and celebrates.
// Mounted globally so it catches setbacks wherever they happen.

import { useEffect, useRef, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import type { Txn } from '@biz-kid/engine';
import { useGameState, useDispatch } from '@biz-kid/store';
import { Card, Eyebrow, Button } from '../ui/kit.js';
import { useLifeDispatch, useLifeComputed } from './store.js';
import { ResilienceScreen, type ResilienceEvent } from './ResilienceScreen.js';
import { useModalSlot } from '../ui/modalQueue.js';

const weekCashflow = (ledger: Txn[], w: number): number =>
  ledger.filter((t) => t.week === w).reduce((s, t) => (t.category === 'capital' ? s : s + t.amount), 0);

export function ResilienceWatcher() {
  const week = useGameState((s) => s.clock.week);
  const ledger = useGameState((s) => s.ledger);
  const inSlump = useGameState((s) => s.inSlump);
  const lastResilienceWeek = useGameState((s) => s.lastResilienceWeek);
  const dispatch = useDispatch();
  const lifeDispatch = useLifeDispatch();
  const freedomPct = useLifeComputed().freedomPct;

  const [event, setEvent] = useState<ResilienceEvent | null>(null);
  const [recovered, setRecovered] = useState(false);
  const prevFreedom = useRef<number | null>(null);

  // Loss-week / recovery detection on each newly-settled week.
  useEffect(() => {
    const settledWeek = week - 1;
    if (settledWeek < 1 || lastResilienceWeek >= settledWeek) return;
    const cf = weekCashflow(ledger, settledWeek);
    if (cf < 0) {
      setEvent({ type: 'lossWeek', week: settledWeek });
      dispatch({ kind: 'SetResilience', week: settledWeek, inSlump: true });
    } else {
      if (inSlump) {
        lifeDispatch({ type: 'ADJUST_MINDSET', deltas: { persistence: 5 } });
        setRecovered(true);
      }
      dispatch({ kind: 'SetResilience', week: settledWeek, inSlump: false });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [week, lastResilienceWeek]);

  // Freedom-Meter drop detection (session-scoped — drops are immediate).
  useEffect(() => {
    if (prevFreedom.current === null) {
      prevFreedom.current = freedomPct;
      return;
    }
    if (freedomPct <= prevFreedom.current - 5 && !event && !inSlump) {
      setEvent({ type: 'freedomDrop', from: prevFreedom.current, to: freedomPct });
    }
    prevFreedom.current = freedomPct;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [freedomPct]);

  // Priority 75 — loss reframe + comeback celebration. Both gated through the
  // queue so they never stack on Payday or a Vision celebration.
  const wantsLoss = !!event;
  const wantsRecovered = recovered;
  const lossSlot = useModalSlot('resilience-loss', wantsLoss, 75);
  const recoveredSlot = useModalSlot('resilience-recovered', wantsRecovered && !wantsLoss, 75);

  return (
    <>
      {event && lossSlot.canShow && <ResilienceScreen event={event} onClose={() => setEvent(null)} />}
      <AnimatePresence>
        {recovered && recoveredSlot.canShow && (
          <motion.div
            key="recovered"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            style={{ position: 'fixed', inset: 0, zIndex: 81, display: 'grid', placeItems: 'center', padding: 18, background: 'rgba(10,14,26,0.6)', backdropFilter: 'blur(7px)', WebkitBackdropFilter: 'blur(7px)' }}
          >
            <motion.div initial={{ y: 16, scale: 0.95 }} animate={{ y: 0, scale: 1 }} style={{ width: '100%', maxWidth: 380 }}>
              <Card style={{ textAlign: 'center' }}>
                <div style={{ fontSize: 48 }}>🌅</div>
                <Eyebrow color="var(--up)">You bounced back</Eyebrow>
                <h2 style={{ margin: '4px 0 2px', fontSize: 22, fontWeight: 900, color: 'var(--text)' }}>Back in the black!</h2>
                <p style={{ margin: '0 0 4px', fontSize: 14, color: 'var(--text-2)', lineHeight: 1.5 }}>
                  You turned a loss week into a profit. That’s real resilience.
                </p>
                <div style={{ display: 'inline-block', margin: '8px 0 14px', padding: '8px 16px', borderRadius: 999, background: 'var(--up-soft)', color: 'var(--up)', fontWeight: 900, fontSize: 15 }}>
                  +5 Persistence 💪
                </div>
                <div>
                  <Button tone="up" size="lg" full onClick={() => setRecovered(false)}>Keep going →</Button>
                </div>
              </Card>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
